import {
  SetCopResponseStateMatchStatusCode,
  SetCopResponseStateName,
} from '@app/finxone-web-frontend/app/lib/actions/cop-response.action';
import {
  AccountDebitCreditSummary,
  AddBeneficiaryRequest,
  BulkPaymentHistoryResponse,
  CheckPayeeResponse,
  DailyBalanceValue,
  GetBeneficiaryResponse,
  GetTotalWealthResponse,
  CancellablePaymentResponse,
  Payee,
  PaymentRequestorRequest,
  PaymentRequestorResponse,
  TransactionDetailResponse,
  TransactionInfoType,
  Transfer,
  TransferMoneyInternationalResponse,
  TransferMoneyRequest,
  TransferMoneyResponse,
  TransferReason,
} from '@app/finxone-web-frontend/app/lib/services/account-service/account.type';
import { PaymentRequestSummary } from '@app/finxone-web-frontend/app/lib/state/notifications.state';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import { PaginatedResponse } from '@app/finxone-web-frontend/app/lib/utils/paginated-response.type';
import { faker } from '@faker-js/faker';
import {
  BeneficiaryViewType,
  PaymentApproverStatus,
  SEPAPaymentRailTypeEnum,
} from '@finxone-platform/form-action';
import { UUID } from '@finxone-platform/shared/base-types';
import {
  ACCOUNTTYPE,
  AccountContextOptions,
  AccountInfoType,
  AccountStatusType,
  AccountTransactionRequest,
  BeneficiaryRequirementsResponse,
  BulkPaymentResultList,
  CardSchemeType,
  CardStatusType,
  CheckPayeeMatchStatus,
  CurrencyExchangeTransactionType,
  DepositDetailsType,
  ExchangeCurrencyTransactionsResponse,
  PaymentRailOptions,
  ScheduledPaymentResponseType,
  TermDepositAccountDetailsType,
  TransactionContextOptions,
  TransactionType,
  emptyAccountInfoType,
} from '@finxone-platform/shared/sys-config-types';
import { PaymentRequestListResponse, PaymentRequestSummaryCounts } from '@finxone-platform/shared/utils';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, map, of } from 'rxjs';

/**
 * Not made injectable so we don't accidently inject this service into main workflow angular app
 */

export class MockAccountService {
  private baseUrl = '';
  private _accounts = new BehaviorSubject<AccountInfoType[]>([]);
  accounts$: Observable<AccountInfoType[]>;
  current_transaction: Transfer | undefined;
  public isCardOnXPaySubject = new BehaviorSubject<boolean | undefined>(false);
  public isCardOnXPay$ = this.isCardOnXPaySubject.asObservable();

  readonly $accounts = of([
    {
      name: 'John Doe',
      balance: '100.00',
      availableBalance: '200.00',
      accountCreatedDate: '2023-10-01',
      currency: 'GBP',
      status: 'ACTIVE',
      customerName: 'John Doe',
      accountId: 'A12247774',
      accountIdentifier: [
        {
          accountNumber: '23456811',
          sortCode: '352434',
          providerExtraInfo: {},
        },
      ],
    },
    {
      name: 'John Doe',
      balance: '100.00',
      availableBalance: '200.00',
      accountCreatedDate: '2023-10-01',
      currency: 'GBP',
      status: 'ACTIVE',
      customerName: 'John Doe',
      accountId: 'A1224778',
      accountIdentifier: [
        {
          accountNumber: '23456812',
          sortCode: '352434',
          providerExtraInfo: {},
        },
      ],
    },
  ]);
  constructor(private store?: Store) {
    this.accounts$ = this.getAccount();
    this.current_transaction = this.getCurrentTransaction();
  }

  private isMultiCurrencyEnabled(): boolean {
    return (
      this.store?.selectSnapshot(ProjectSettingsState.getProjectSettings)?.projectConfig?.account_service
        ?.andariaConfig?.useMultiCurrency ?? false
    );
  }

  public currentCardIsOnXPay(currentCardMaskedPan: string) {}

  getMainAccountId(): Observable<string> {
    return of('A12247774');
  }
  getAccountCard(accountId: string) {
    const v = {
      result: [
        {
          id: 'V1200137Q2',
          maskedPan: '412345******2683',
          expiry: '2027-08',
          status: CardStatusType.ACTIVE,
          currency: 'GBP',
          limit: 100.0,
          maxLimit: 1000000.0,
          spend: 0.0,
          cardType: '',
          type: 'CONSUMER',
          scheme: CardSchemeType.VISA,
          format: 'PHYSICAL',
          userRef: '54c45c62-8aae-4d6f-8657-6f4826cae025',
          holder: {
            firstName: 'John',
            lastName: 'Doe',
          },
          shippingAddress: {
            city: 'London',
            country: 'GB',
            addressLine1: '',
            postCode: 'NW10AU',
          },
          createdDate: '25/08/2023',
          activatedDate: '28/08/2023',
        },
        {
          id: 'V1200137Q2',
          maskedPan: '412345******2683',
          expiry: '2027-08',
          status: CardStatusType.ACTIVE,
          currency: 'GBP',
          limit: 100.0,
          maxLimit: 1000000.0,
          spend: 0.0,
          cardType: '',
          type: 'CONSUMER',
          scheme: CardSchemeType.VISA,
          format: 'VIRTUAL',
          userRef: '54c45c62-8aae-4d6f-8657-6f4826cae025',
          holder: {
            firstName: 'John',
            lastName: 'Doe',
          },
          shippingAddress: {
            city: 'London',
            country: 'GB',
            addressLine1: '',
            postCode: 'NW10AU',
          },
          createdDate: '25/08/2023',
          activatedDate: '28/08/2023',
        },
      ],
      totalPages: 1,
      page: 0,
      limit: 1,
    };
    return of(v);
  }

  public getBeneficiaryRequirements(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    countryCode: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    currencyCode: string,
  ): Observable<BeneficiaryRequirementsResponse> {
    return of({
      beneficiaryBankAccount: {
        requirements: {
          beneficiaryAddress: 'required',
        },
        iban: {
          requirement: 'none',
        },
        accountNumber: {
          regex: '^[0-9]{7,20}$',
          requirement: 'required',
        },
        bankName: {
          requirement: 'none',
        },
        routingCodes: {
          'sort-code': {
            regex: '^[0-9]{6}$',
            requirement: 'required',
          },
          bic: {
            regex: '^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$',
            requirement: 'optional',
          },
          aba: {
            regex: '^(?!(?:0{9}))(?:\\d{9})$',
            requirement: 'required',
          },
        },
      },
      beneficiaryAddress: {
        country: {
          regex: '^[A-Z]{2}$',
          requirement: 'required',
        },
        region: {
          regex: '^.{1,255}',
          requirement: 'required',
        },
        city: {
          regex: '^.{1,255}',
          requirement: 'required',
        },
        houseNumber: {
          regex: '^.{1,255}',
          requirement: 'optional',
        },
        postalCode: {
          regex: '^.{1,12}',
          requirement: 'required',
        },
        refinement: {
          regex: '^.{1,255}',
          requirement: 'optional',
        },
        addressLine1: {
          regex: '^.{1,255}',
          requirement: 'required',
        },
        addressLine2: {
          regex: '^.{1,255}',
          requirement: 'optional',
        },
        addressLine3: {
          regex: '^.{1,255}',
          requirement: 'optional',
        },
        addressLine4: {
          regex: '^.{1,255}',
          requirement: 'optional',
        },
      },
    });
  }

  getTransactionSummary(
    accountId: string,
    params: AccountTransactionRequest,
  ): Observable<
    PaginatedResponse<
      TransactionInfoType | ExchangeCurrencyTransactionsResponse | ScheduledPaymentResponseType
    >
  > {
    if (params.transactionContext === TransactionContextOptions.SAVINGS_TRANSACTION) {
      // TODO make savings specific call
      return of({
        result: this.dummySavingsTransactionHistory(params.size),
        totalPages: 4,
        page: 1,
        limit: params.size,
        totalItems: params.size * 4,
      });
    } else if (params.transactionContext === TransactionContextOptions.SCHEDULED_PAYMENTS) {
      if (params.accountContext === AccountContextOptions.SAVINGS_ACCOUNT) {
        return of({
          result: this.dummyScheduledPaymentWithdrawals(),
          totalPages: 1,
          page: 1,
          limit: params.size,
          totalItems: 4,
        });
      }
      return of({
        result: this.dummyScheduledPayments(params.size),
        totalPages: 4,
        page: 1,
        limit: params.size,
        totalItems: params.size * 4,
      });
    }

    if (
      params.type === CurrencyExchangeTransactionType.CURRENCYALL ||
      params.type === CurrencyExchangeTransactionType.CURRENCYINCOMING ||
      params.type === CurrencyExchangeTransactionType.CURRENCYOUTGOING
    ) {
      return of({
        result: this.dummyDataCurrencyExchangeHistory(params.size),
        totalPages: 1,
        page: 1,
        limit: 10,
        totalItems: 20,
      });
    } else {
      return of({
        result: this.dummyDataTxHistory(params.size),
        totalPages: 1,
        page: 1,
        limit: 10,
        totalItems: 20,
      });
    }
  }

  private dummyScheduledPayments(n: number): ScheduledPaymentResponseType[] {
    const result: ScheduledPaymentResponseType[] = [];

    const now = new Date();
    const runningDate = new Date(now);
    runningDate.setDate(now.getDate() + 10);

    for (let step = 0; step < n; step++) {
      const scheduleDate = new Date(runningDate);
      scheduleDate.setDate(runningDate.getDate() + step);

      const amount = faker.number.float({
        min: 10,
        max: 1000,
        fractionDigits: 2,
      });

      const fullName = faker.person.fullName();
      const sortCode = faker.finance.routingNumber();
      const accountNumber = faker.finance.accountNumber(8);

      const scheduledPayment: ScheduledPaymentResponseType = {
        id: faker.string.uuid(),
        data: {
          bid: faker.string.uuid(),
          name: fullName,
          amount,
          bankId: faker.string.uuid(),
          userId: faker.string.uuid(),
          currency: 'GBP',
          sortCode,
          accountType: 'CURRENT',
          accountNumber,
          sourceAccountId: faker.string.uuid(),
          paymentReference: faker.lorem.words(3),
          transferReasonId: faker.string.uuid(),
          schedulePaymentDate: scheduleDate,
          transferReasonIdText: faker.lorem.words(2),
          paymentReasonCategory: faker.helpers.arrayElement(['PERSONAL', 'BUSINESS']),
          destinationIdentifier: {
            id: faker.string.uuid(),
            country: 'GB',
            currency: 'GBP',
            sortCode,
            accountNumber,
          },
        },
      };

      result.push(scheduledPayment);
    }

    return result;
  }

  private dummyScheduledPaymentWithdrawals(): ScheduledPaymentResponseType[] {
    const emptyDataDefaults = {
      bid: '',
      name: '',
      bankId: '',
      userId: '',
      sortCode: '',
      accountType: '',
      accountNumber: '',
      transferReasonIdText: '',
      destinationIdentifier: { id: '', country: '', currency: '', sortCode: '', accountNumber: '' },
      paymentReasonCategory: '',
    };
    return [
      {
        id: '91afa292-8ba5-47c3-a9f6-97dc905f1d29',
        data: {
          ...emptyDataDefaults,
          sourceAccountId: '92367849-76df-4fb9-935c-717c680cb08c',
          amount: 10,
          currency: 'GBP',
          paymentReference: 'Schedule Testing 05/27/2026',
          schedulePaymentDate: '2026-05-27T00:00:00.000Z',
          transferReasonId: '',
        },
      },
      {
        id: '234cc0c9-db54-4b59-aebb-268fe7c042d0',
        data: {
          ...emptyDataDefaults,
          sourceAccountId: '92367849-76df-4fb9-935c-717c680cb08c',
          amount: 25,
          currency: 'GBP',
          paymentReference: 'Schedule Testing 05/29/2026',
          schedulePaymentDate: '2026-05-29T00:00:00.000Z',
          transferReasonId: '',
        },
      },
      {
        id: '234cc0c9-db54-4b59-aebb-268fe7c042d1',
        data: {
          ...emptyDataDefaults,
          sourceAccountId: '92367849-76df-4fb9-935c-717c680cb08c',
          amount: 15,
          currency: 'GBP',
          paymentReference: 'Schedule Testing 05/20/2026',
          schedulePaymentDate: '2026-05-20T00:00:00.000Z',
          transferReasonId: '',
        },
      },
      {
        id: '234cc0c9-db54-4b59-aebb-268fe7c042d2',
        data: {
          ...emptyDataDefaults,
          sourceAccountId: '92367849-76df-4fb9-935c-717c680cb08c',
          amount: 50,
          currency: 'GBP',
          paymentReference: 'Schedule Testing 03/29/2026',
          schedulePaymentDate: '2026-03-29T00:00:00.000Z',
          transferReasonId: '',
        },
      },
    ];
  }

  private dummySavingsTransactionHistory(n: number) {
    const result = [];
    // Get current date
    const now = new Date();

    // Go back 20 days
    const runningDate = new Date(now);
    runningDate.setDate(now.getDate() - 20);

    let runningBalance = 10000;
    for (let step = 0; step < n; step++) {
      const randomHours = faker.number.int({ min: 18, max: 48 });

      // Add random minutes to the date
      const finalDate = new Date(runningDate);
      finalDate.setHours(finalDate.getHours() + randomHours);
      runningDate.setHours(finalDate.getHours() + randomHours);
      const txAmount = faker.number.float({ min: 10, max: 1000, fractionDigits: 2 });

      const creditOrDebit = Math.random() < 0.5 ? 'debit' : 'credit';

      if (creditOrDebit === 'debit') {
        runningBalance -= txAmount;
      } else {
        runningBalance += txAmount;
      }

      const receiver = faker.person.fullName();

      result.push({
        currency: 'GBP',
        amount: txAmount,
        description: creditOrDebit === 'debit' ? `Withdrawal to nominated account` : `Deposit`,
        transactionDate: finalDate.toISOString(),
        postedDate: '',
        transactionType: creditOrDebit,
        status: Math.random() < 0.8 ? 'completed' : Math.random() < 0.5 ? 'pending' : 'processing',
        id: '20241112-6DY3NW',
        transactionNumber: '20250407-R93GO3',
        accountBalance: runningBalance,
        senderAccount: {
          ledgerNumber: '11956382',
          alias: 'GBP Account',
          accountHolderName: 'Tom',
          accountNumber: null,
          iban: null,
          country: 'GB',
          currency: 'GBP',
          routingCodes: {
            bic: null,
          },
        },
        receiverAccount: {
          ledgerNumber: '43956385',
          alias: 'GBP Account',
          accountHolderName: receiver,
          accountNumber: null,
          iban: null,
          country: 'GB',
          currency: 'GBP',
          routingCodes: {},
        },
        feeAmount: faker.number.float({ min: 0, max: 10, fractionDigits: 2 }),
        feeCurrency: 'GBP',
      });
    }
    return result.reverse();
  }

  private dummyDataTxHistory(n: number) {
    const result = [];
    // Get current date
    const now = new Date();

    // Go back 20 days
    const runningDate = new Date(now);
    runningDate.setDate(now.getDate() - 20);

    let runningBalance = 10000;
    for (let step = 0; step < n; step++) {
      const randomHours = faker.number.int({ min: 18, max: 48 });

      // Add random minutes to the date
      const finalDate = new Date(runningDate);
      finalDate.setHours(finalDate.getHours() + randomHours);
      runningDate.setHours(finalDate.getHours() + randomHours);
      const txAmount = faker.number.float({ min: 10, max: 1000, fractionDigits: 2 });

      const creditOrDebit = Math.random() < 0.5 ? 'debit' : 'credit';

      if (creditOrDebit === 'debit') {
        runningBalance -= txAmount;
      } else {
        runningBalance += txAmount;
      }

      const receiver = faker.person.fullName();

      result.push({
        currency: 'GBP',
        amount: txAmount,
        description: `Payment to ${receiver} - ${faker.string.alpha({
          casing: 'upper',
          length: { min: 3, max: 3 },
        })}${faker.number.int({ min: 1000000, max: 9999999 })}`,
        transactionDate: finalDate.toISOString(),
        postedDate: '',
        transactionType: creditOrDebit,
        status: Math.random() < 0.8 ? 'completed' : Math.random() < 0.5 ? 'pending' : 'processing',
        id: '20241112-6DY3NW',
        transactionNumber: '20250407-R93GO3',
        accountBalance: runningBalance,
        senderAccount: {
          ledgerNumber: '11956382',
          alias: 'GBP Account',
          accountHolderName: 'Tom',
          accountNumber: null,
          iban: null,
          country: 'GB',
          currency: 'GBP',
          routingCodes: {
            bic: null,
          },
        },
        receiverAccount: {
          ledgerNumber: '43956385',
          alias: 'GBP Account',
          accountHolderName: receiver,
          accountNumber: null,
          iban: null,
          country: 'GB',
          currency: 'GBP',
          routingCodes: {},
        },
        feeAmount: faker.number.float({ min: 0, max: 10, fractionDigits: 2 }),
        feeCurrency: 'GBP',
      });
    }
    return result.reverse();
  }
  private dummyDataCurrencyExchangeHistory(n: number) {
    const result = [];
    for (let step = 0; step < n; step++) {
      result.push({
        buyAmount: 12.4,
        sellAmount: 10.0,
        buyCurrency: 'EUR',
        sellCurrency: 'GBP',
        currency: 'GBP',
        amount: 150,
        description: 'Sold',
        transactionDate: '2023-01-04',
        transactionType: Math.random() < 0.5 ? 'debit' : 'credit',
        status: 'completed',
        id: '1234127789',
        quoteId: '176263198',
        transactionNumber: '16556257',
        rate: 1231,
        buyAccountId: '1276637',
        sellAccountId: '6577879',
      });
    }
    return result;
  }
  getMonthlySummary(accountId: string): Observable<AccountDebitCreditSummary> {
    return of({
      total_credits: 100.0,
      total_debits: 100.0,
      last_updated: '2023-01-04T06:33:48.017Z',
    });
  }
  getAccount(): Observable<AccountInfoType[]> {
    const v = [
      {
        ...emptyAccountInfoType(),
        name: 'My GBP Account',
        balance: '100.00',
        availableBalance: '200.00',
        ledgerBalance: '100.00',
        ledgerBalanceIncludingPendingExchange: '100.00',
        accountCreatedDate: '2023-10-01',
        currency: 'GBP',
        country: 'GB',
        status: AccountStatusType.active,
        customerName: 'John Doe',
        accountId: 'A1224777',
        accountIdentifier: [
          {
            accountNumber: '23456811',
            sortCode: '352434',
            providerExtraInfo: {},
          },
        ],
      },
      {
        ...emptyAccountInfoType(),
        accountType: 'multi-currency',
        name: 'My Multi Currency Account',

        balance: ['GBP', 'INR', 'USD', 'EUR', 'AUD', 'AED'].map((c) => ({
          currency: c,
          amount: '120000.00',
        })),
        availableBalance: ['GBP', 'INR', 'USD', 'EUR', 'AUD', 'AED'].map((c) => ({
          currency: c,
          amount: '110000.00',
        })),
        ledgerBalance: ['GBP', 'INR', 'USD', 'EUR', 'AUD', 'AED'].map((c) => ({
          currency: c,
          amount: '120000.00',
        })),
        ledgerBalanceIncludingPendingExchange: ['GBP', 'INR', 'USD', 'EUR', 'AUD', 'AED'].map((c) => ({
          currency: c,
          amount: '120000.00',
        })),
        accountCreatedDate: '2023-10-01',
        currency: ['GBP', 'INR', 'USD', 'EUR', 'AUD', 'AED'].map((c) => ({
          currency: c,
        })),
        country: 'GB',
        status: AccountStatusType.active,
        customerName: 'John Doe',
        accountId: 'A1224771',
        accountIdentifier: [
          {
            accountNumber: '23456811',
            sortCode: '352434',
            providerExtraInfo: {},
          },
        ],
      },
      {
        ...emptyAccountInfoType(),
        name: 'John Doe',
        balance: '220.00',
        availableBalance: '200.00',
        indicativeBalance: '210.00',
        ledgerBalance: '220.00',
        ledgerBalanceIncludingPendingExchange: '220.00',
        scheduledExchanges: {
          netTotal: 10,
          totalBooked: 3,
        },
        accountCreatedDate: '2023-10-01',
        currency: 'GBP',
        country: 'GB',
        status: AccountStatusType.active,
        customerName: 'John Doe',
        accountId: 'A1224778',
        accountIdentifier: [
          {
            accountNumber: '23456812',
            sortCode: '352434',
            providerExtraInfo: {},
          },
        ],
      },
    ] as AccountInfoType[];
    return of(this.isMultiCurrencyEnabled() ? v : v.filter((a) => a.accountType !== 'multi-currency'));
  }

  getCurrentTransaction(): Transfer {
    const v = {
      beneficiary: {
        id: '123',
        name: 'John Doe',
        accountType: ACCOUNTTYPE.PERSONAL,
        destinationIdentifier: {
          accountNumber: 'A1224777',
          sortCode: '352434',
          currency: 'GBP',
          country: 'GB',
          bic: '',
          iban: '',
          aba: '',
          rtnCanada: '',
          clabe: '',
          bsbCode: '',
          ifsc: '',
          cnaps: '',
        },
      },
      transactionDate: new Date(Date.now()),
      transferRequest: {
        sourceAccountId: 'A1224777',
        transferReasonId: '1',
        bid: '10',
        amount: 10,
        paymentReference: 'Payment Reference Description',
        paymentRailOption: SEPAPaymentRailTypeEnum.SEPA_CREDIT_TRANSFER,
      },
    };
    return v;
  }

  getAccounts(page = 1, size = 10): Observable<PaginatedResponse<AccountInfoType>> {
    const v = {
      result: [
        {
          ...emptyAccountInfoType(),
          name: 'GBP Account',
          balance: '899.00',
          availableBalance: '300.00',
          indicativeBalance: '220.00',
          ledgerBalance: '899.00',
          ledgerBalanceIncludingPendingExchange: '899.00',
          scheduledExchanges: {
            netTotal: 20,
            totalBooked: 2,
          },
          accountCreatedDate: '2023-10-01',
          currency: 'GBP',
          country: 'GB',
          customerId: 'C121Y676',
          customerName: 'John Doe',
          accountId: 'A1224777',
          status: AccountStatusType.active,
          accountIdentifier: [
            {
              accountNumber: '8331825789',
              ledgerNumber: '8331876357',
              sortCode: '13-24-35',
              iban: 'IBAN13986451093841396673',
              bic: 'TCCLGB3L',
              providerExtraInfo: {
                internal: {
                  accountHolderName: 'James Ramsey',
                  country: 'US',
                  currency: 'USD',
                  routingCodes: {
                    aba: '',
                  },
                  accountNumber: '8331876357',
                  iban: null,
                  ledgerNumber: '44582584',
                  reference: null,
                  bankName: null,
                  bankAddress: null,
                  intermediary: null,
                },
                wire: {
                  accountHolderName: 'James Ramsey',
                  country: 'US',
                  currency: 'USD',
                  routingCodes: {
                    aba: '346022173',
                  },
                  accountNumber: '7331698357',
                  iban: null,
                  ledgerNumber: null,
                  reference: null,
                  bankName: 'Community Federal Savings Bank',
                  bankAddress: '810 Seventh Avenue, New York, NY 10019, US',
                  intermediary: null,
                },
                ach: {
                  accountHolderName: 'James Ramsey',
                  country: 'US',
                  currency: 'USD',
                  routingCodes: {
                    aba: '026022150',
                  },
                  accountNumber: '7331698357',
                  iban: null,
                  ledgerNumber: null,
                  reference: null,
                  bankName: 'Community Federal Savings Bank',
                  bankAddress: '810 Seventh Avenue, New York, NY 10019, US',
                  intermediary: null,
                },
                swift: {
                  accountHolderName: 'James Ramsey',
                  country: 'GB',
                  currency: 'USD',
                  routingCodes: {
                    bic: 'TCCLGB3L',
                  },
                  accountNumber: '7331698355',
                  iban: 'GB57TCCL04140416453849',
                  ledgerNumber: null,
                  reference: null,
                  bankName: 'The Currency Cloud Limited',
                  bankAddress: '12 Steward Street, The Steward Building, London, E1 6FQ, GB',
                  intermediary: {
                    country: 'GB',
                    currency: 'USD',
                    routingCodes: {
                      bic: 'BARNBB22',
                    },
                    bankName: 'Barclays Bank PLC',
                  },
                },
              },
            },
          ],
          spendingBudget: {
            isActive: true,
            limit: 1000,
            spent: 500,
            remaining: 500,
            resetDay: 1,
            periodStart: '',
            periodEnd: '',
          },
        },

        {
          ...emptyAccountInfoType(),
          accountType: 'multi-currency',
          name: 'My Multi Currency Account',

          balance: ['GBP', 'INR', 'USD', 'EUR', 'AUD', 'AED'].map((c) => ({
            currency: c,
            amount: '120000.00',
          })),
          availableBalance: ['GBP', 'INR', 'USD', 'EUR', 'AUD', 'AED'].map((c) => ({
            currency: c,
            amount: '110000.00',
          })),
          ledgerBalance: ['GBP', 'INR', 'USD', 'EUR', 'AUD', 'AED'].map((c) => ({
            currency: c,
            amount: '120000.00',
          })),
          ledgerBalanceIncludingPendingExchange: ['GBP', 'INR', 'USD', 'EUR', 'AUD', 'AED'].map((c) => ({
            currency: c,
            amount: '120000.00',
          })),
          accountCreatedDate: '2023-10-01',
          currency: ['GBP', 'INR', 'USD', 'EUR', 'AUD', 'AED'].map((c) => ({
            currency: c,
          })),
          country: 'GB',
          status: AccountStatusType.active,
          customerName: 'John Doe',
          accountId: 'A1224773',
          accountIdentifier: [
            {
              accountNumber: '8331825789',
              ledgerNumber: '8331876357',
              sortCode: '13-24-35',
              iban: 'IBAN13986451093841396673',
              bic: 'TCCLGB3L',
              providerExtraInfo: {
                internal: {
                  accountHolderName: 'James Ramsey',
                  country: 'US',
                  currency: 'USD',
                  routingCodes: {
                    aba: '',
                  },
                  accountNumber: '8331876357',
                  iban: null,
                  ledgerNumber: '44582584',
                  reference: null,
                  bankName: null,
                  bankAddress: null,
                  intermediary: null,
                },
                wire: {
                  accountHolderName: 'James Ramsey',
                  country: 'US',
                  currency: 'USD',
                  routingCodes: {
                    aba: '346022173',
                  },
                  accountNumber: '7331698357',
                  iban: null,
                  ledgerNumber: null,
                  reference: null,
                  bankName: 'Community Federal Savings Bank',
                  bankAddress: '810 Seventh Avenue, New York, NY 10019, US',
                  intermediary: null,
                },
                ach: {
                  accountHolderName: 'James Ramsey',
                  country: 'US',
                  currency: 'USD',
                  routingCodes: {
                    aba: '026022150',
                  },
                  accountNumber: '7331698357',
                  iban: null,
                  ledgerNumber: null,
                  reference: null,
                  bankName: 'Community Federal Savings Bank',
                  bankAddress: '810 Seventh Avenue, New York, NY 10019, US',
                  intermediary: null,
                },
                'faster-payments': {
                  accountHolderName: 'James Ramsey',
                  country: 'GB',
                  currency: 'USD',
                  routingCodes: {
                    bic: 'TCCLGB3L',
                    'sort-code': '041404',
                  },
                  accountNumber: '73316983',
                  iban: 'GB57TCCL04140416453849',
                },
                sepa: {
                  accountHolderName: 'James Ramsey',
                  country: 'GB',
                  currency: 'USD',
                  routingCodes: {
                    bic: 'TCCLGB3L',
                  },
                  iban: 'GB57TCCL04140416453849',
                },
                swift: {
                  accountHolderName: 'James Ramsey',
                  country: 'GB',
                  currency: 'USD',
                  routingCodes: {
                    bic: 'TCCLGB3L',
                  },
                  accountNumber: '7331698355',
                  iban: 'GB57TCCL04140416453849',
                  ledgerNumber: null,
                  reference: null,
                  bankName: 'The Currency Cloud Limited',
                  bankAddress: '12 Steward Street, The Steward Building, London, E1 6FQ, GB',
                  intermediary: {
                    country: 'GB',
                    currency: 'USD',
                    routingCodes: {
                      bic: 'BARNBB22',
                    },
                    bankName: 'Barclays Bank PLC',
                  },
                },
              },
            },
          ],
        },

        {
          ...emptyAccountInfoType(),
          accountType: 'multi-currency',
          name: 'My Second Multi Currency Account',

          balance: ['BBD', 'CAD', 'ZAR'].map((c) => ({
            currency: c,
            amount: '293000.00',
          })),
          availableBalance: ['BBD', 'CAD', 'ZAR'].map((c) => ({
            currency: c,
            amount: '292939.23',
          })),
          ledgerBalance: ['BBD', 'CAD', 'ZAR'].map((c) => ({
            currency: c,
            amount: '292939.23',
          })),
          ledgerBalanceIncludingPendingExchange: ['BBD', 'CAD', 'ZAR'].map((c) => ({
            currency: c,
            amount: '292939.23',
          })),
          accountCreatedDate: '2023-10-01',
          currency: ['BBD', 'CAD', 'ZAR'].map((c) => ({
            currency: c,
          })),
          country: 'GB',
          status: AccountStatusType.active,
          customerName: 'John Doe',
          accountId: 'A1224775',
          accountIdentifier: [
            {
              ledgerNumber: '8331876357',
              iban: 'GB57TCCL08473776782792',
              bic: 'TCCLGB3L',
              providerExtraInfo: {
                internal: {
                  accountHolderName: 'James Ramsey',
                  country: 'US',
                  currency: 'USD',
                  routingCodes: {
                    aba: '',
                  },
                  accountNumber: '',
                  iban: null,
                  ledgerNumber: '44582584',
                  reference: null,
                  bankName: null,
                  bankAddress: null,
                  intermediary: null,
                },
                swift: {
                  accountHolderName: 'James Ramsey',
                  country: 'GB',
                  currency: 'USD',
                  routingCodes: {
                    bic: 'TCCLGB3L',
                  },
                  accountNumber: '7331698355',
                  iban: 'GB57TCCL08473776782792',
                  ledgerNumber: null,
                  reference: null,
                  bankName: 'The Currency Cloud Limited',
                  bankAddress: '12 Steward Street, The Steward Building, London, E1 6FQ, GB',
                  intermediary: {
                    country: 'GB',
                    currency: 'USD',
                    routingCodes: {
                      bic: 'BARNBB22',
                    },
                    bankName: 'Barclays Bank PLC',
                  },
                },
              },
            },
          ],
        },
        {
          ...emptyAccountInfoType(),
          name: 'USD Account',
          balance: '100.00',
          availableBalance: '200.00',
          indicativeBalance: '165.00',
          ledgerBalance: '100.00',
          ledgerBalanceIncludingPendingExchange: '100.00',
          accountCreatedDate: '2023-10-01',
          currency: 'USD',
          country: 'US',
          customerId: 'C121Y676',
          status: AccountStatusType.declined,
          customerName: 'John Doe',
          accountId: 'A1224778',
          accountIdentifier: [
            {
              accountNumber: '8331824357',
              bic: 'TCCLGB3L',
              providerExtraInfo: {},
            },
          ],
          spendingBudget: {
            isActive: true,
            limit: 3000,
            spent: 500,
            remaining: 2500,
            resetDay: 1,
            periodStart: '',
            periodEnd: '',
          },
        },
        {
          ...emptyAccountInfoType(),
          name: 'ZAR Account',
          balance: '123.00',
          availableBalance: '200.00',
          indicativeBalance: '165.00',
          ledgerBalance: '123.00',
          ledgerBalanceIncludingPendingExchange: '123.00',
          scheduledExchanges: {
            netTotal: -35.0,
            totalBooked: 1,
          },
          accountCreatedDate: '2023-10-01',
          currency: 'ZAR',
          country: 'ZA',
          customerId: 'C121Y676',
          status: AccountStatusType.closed,
          customerName: 'John Doe',
          accountId: 'A1224779',
          accountIdentifier: [
            {
              iban: 'IBAN13986451093841396475',
              bic: 'TCCLGB3L',
              providerExtraInfo: {},
            },
          ],
          spendingBudget: {
            isActive: true,
            limit: 1000,
            spent: 900,
            remaining: 100,
            resetDay: 1,
            periodStart: '',
            periodEnd: '',
          },
        },
      ],
      selectedAccountDetail: {
        ...emptyAccountInfoType(),
        name: 'John Doe',
        balance: '100.00',
        availableBalance: '200.00',
        indicativeBalance: '165.00',
        ledgerBalance: '100.00',
        ledgerBalanceIncludingPendingExchange: '100.00',
        accountCreatedDate: '2023-10-01',
        currency: 'GBP',
        country: 'GB',
        customerId: 'C121Y676',
        status: AccountStatusType.suspended,
        customerName: 'John Doe',
        accountId: 'A1224780',
        accountIdentifier: [
          {
            accountNumber: '23456812',
            sortCode: '352434',
            iban: 'IBAN13986451093841396475',
            bic: 'TCCLGB3L',
            providerExtraInfo: {},
          },
        ],
        spendingBudget: {
          isActive: true,
          limit: 500,
          spent: 500,
          remaining: 0,
          resetDay: 1,
          periodStart: '',
          periodEnd: '',
        },
      },
      totalPages: 1,
      totalItems: 10,
      page: page,
      limit: size,
    };
    if (!this.isMultiCurrencyEnabled()) {
      v.result = v.result.filter((a) => a.accountType !== 'multi-currency');
    }
    return of(v as PaginatedResponse<AccountInfoType>);
  }

  private dummyDataBeneficiary(n: number) {
    const result = [];
    for (let step = 0; step < n; step++) {
      result.push({
        id: new UUID().toString(),
        accountType: ACCOUNTTYPE.PERSONAL,
        amount: 2000,
        paymentDate: '2025-06-16',
        name: 'Jason Brody',
        destinationIdentifier: {
          accountNumber: '12312312',
          currency: 'USD',
          country: 'US',
          bic: '',
          ifsc: '',
          aba: '777777233',
          bsbCode: '',
          rtnCanada: '',
          clabe: '',
          bankCode: '',
          branchCode: '',
          id: new UUID().toString(),
          bankName: '',
          sortCode: '',
          iban: '',
          cnaps: '',
        },
        address: {
          addressLine1: '5160 Sunnyvale Dr',
          city: 'sinsinati',
          state: 'Utah',
          country: 'US',
          postCode: '90210',
        },
      });
    }
    return result;
  }

  getBeneficiary(
    page = 1,
    size = 100,
    query?: string,
    currency?: string,
    beneficiaryViewType?: BeneficiaryViewType,
  ): Observable<PaginatedResponse<GetBeneficiaryResponse>> {
    const allBeneficiaryData = [
      {
        id: 'b8e8f007-11d0-4fce-8318-e4e03760b6c2',
        accountType: ACCOUNTTYPE.PERSONAL,
        name: 'Jason Brody',
        isFavorite: true,
        amount: 1000,
        paymentDate: '2025-07-30',
        destinationIdentifier: {
          accountNumber: '12312312',
          currency: 'USD',
          country: 'US',
          bic: '',
          ifsc: '',
          aba: '777777233',
          bsbCode: '',
          rtnCanada: '',
          clabe: '',
          bankCode: '',
          branchCode: '',
          id: 'e49228f1-119f-4e43-bd97-24c9175c2ae3',
          bankName: '',
          sortCode: '',
          iban: '',
          cnaps: '',
        },
        address: {
          addressLine1: '5160 Sunnyvale Dr',
          city: 'sinsinati',
          state: 'Utah',
          country: 'US',
          postCode: '90210',
        },
      },
      {
        id: 'B120062W7Z',
        name: 'John Doe',
        accountType: ACCOUNTTYPE.PERSONAL,
        amount: 1400,
        paymentDate: '2024-07-30',
        destinationIdentifier: {
          accountNumber: '19827354',
          sortCode: '345678',
          currency: 'GBP',
          country: 'US',
          iban: '',
          bic: '',
          aba: '',
          rtnCanada: '',
          clabe: '',
          bsbCode: '',
          ifsc: '',
          cnaps: '',
          countrySpecificDetails: { business: false },
        },
      },
      {
        id: 'D341141Y1B',
        name: 'Brandon Taylor',
        isFavorite: true,
        amount: 2000,
        paymentDate: '2025-06-16',
        accountType: ACCOUNTTYPE.PERSONAL,
        destinationIdentifier: {
          accountNumber: '1357924580',
          sortCode: '223344',
          currency: 'USD',
          country: 'US',
          iban: '',
          bic: 'ABCDUS12345',
          aba: '026009593',
          rtnCanada: '',
          clabe: '',
          bsbCode: '',
          ifsc: '',
          cnaps: '',
          countrySpecificDetails: { business: false },
        },
        address: {
          addressLine1: '5160 Sunnyvale Dr',
          street: '5160 Sunnyvale Dr',
          city: 'Salt Lake City',
          state: 'Utah',
          country: 'US',
          postalCode: '90210',
          postCode: '90210',
        },
      },
      ...(size > 3 ? this.dummyDataBeneficiary(size - 3) : []),
    ] as GetBeneficiaryResponse[];

    // Filter based on view type
    const filteredData =
      beneficiaryViewType === BeneficiaryViewType.FAVORITE_LIST
        ? allBeneficiaryData.filter((b) => b.isFavorite)
        : allBeneficiaryData; // Default: all beneficiaries

    const paginatedData = {
      result: filteredData,
      totalPages: 1,
      page: page,
      limit: size,
      totalItems: 5,
    };

    return of(paginatedData);
  }

  getBeneficiaryDetail(bid: string): Observable<GetBeneficiaryResponse> {
    return of({
      id: 'c691060c-bb7d-4f87-aaf3-a36db7b26901',
      name: 'Brandon Taylor',
      accountType: ACCOUNTTYPE.PERSONAL,
      createdDate: '2024-01-11T16:04:15.317Z',
      updatedDate: '2024-02-18T16:04:15.317Z',
      deletedDate: '2024-03-20T16:05:17.317Z',
      destinationIdentifier: {
        accountNumber: '1357924580',
        sortCode: '',
        currency: 'USD',
        country: 'US',
        iban: '',
        bic: 'ABCDUS12345',
        aba: '026009593',
        rtnCanada: '',
        clabe: '',
        bsbCode: '',
        ifsc: '',
        cnaps: '',
        countrySpecificDetails: { business: false },
        bankName: 'Bank of America',
      },
      address: {
        addressLine1: '5160 Sunnyvale Dr',
        street: 'street',
        city: 'Salt Lake City',
        state: 'Utah',
        country: 'US',
        postalCode: '90210',
      },
    });
  }

  checkWebhookNotification() {
    const v = [
      {
        name: 'John Doe',
        balance: '100.00',
        availableBalance: '200.00',
        currency: 'GBP',
        customerId: 'C121Y676',
        status: 'ACTIVE',
        customerName: 'John Doe',
        accountId: 'A1224777',
      },
    ];
    return of(v);
  }

  getChartData(parms: string): Observable<DailyBalanceValue[]> {
    return of([
      {
        date: '2023-02-01',
        balance: 100,
      },
      {
        date: '2023-02-02',
        balance: 200,
      },
      {
        date: '2023-02-03',
        balance: 300,
      },
      {
        date: '2023-02-04',
        balance: 400,
      },
      {
        date: '2023-02-05',
        balance: 500,
      },
      {
        date: '2023-02-06',
        balance: 500,
      },
      {
        date: '2023-02-07',
        balance: 500,
      },
      {
        date: '2023-02-08',
        balance: 500,
      },
      {
        date: '2023-02-09',
        balance: 600,
      },
      {
        date: '2023-02-10',
        balance: 600,
      },
      {
        date: '2023-02-11',
        balance: 600,
      },
      {
        date: '2023-02-12',
        balance: 600,
      },
      {
        date: '2023-02-13',
        balance: 700,
      },
      {
        date: '2023-02-14',
        balance: 700,
      },
      {
        date: '2023-02-15',
        balance: 700,
      },
      {
        date: '2023-02-16',
        balance: 700,
      },
      {
        date: '2023-02-17',
        balance: 600,
      },
      {
        date: '2023-02-18',
        balance: 600,
      },
      {
        date: '2023-02-19',
        balance: 800,
      },
      {
        date: '2023-02-20',
        balance: 800,
      },
      {
        date: '2023-02-22',
        balance: 600,
      },
      {
        date: '2023-02-23',
        balance: 700,
      },
      {
        date: '2023-02-24',
        balance: 800,
      },
      {
        date: '2023-02-25',
        balance: 700,
      },
      {
        date: '2023-02-26',
        balance: 700,
      },
      {
        date: '2023-02-27',
        balance: 900,
      },
      {
        date: '2023-02-28',
        balance: 800,
      },
    ]);
  }

  getAccountDataByUserId() {
    const v = [
      {
        name: 'John Doe',
        balance: '100.00',
        availableBalance: '200.00',
        indicativeBalance: '200.00',
        ledgerBalance: '100.00',
        ledgerBalanceIncludingPendingExchange: '100.00',
        accountCreatedDate: '2023-10-01',
        currency: 'GBP',
        customerId: 'C121Y676',
        status: 'ACTIVE',
        customerName: 'John Doe',
        accountId: 'A1224777',
        accountIdentifier: [
          {
            accountNumber: '23456812',
            sortCode: '352434',
            providerExtraInfo: {},
          },
        ],
        cards: [],
      },
    ];
    return of(v);
  }
  getCards() {
    const v = {
      result: [
        {
          id: 'V120012MR8',
          maskedNo: '412345******5159',
          expiry: '2024-08',
          status: 0,
          currency: 'GBP',
          cardLimit: '100.00',
          cardMaxLimit: '1000000.00',
          cardSpend: '0.00',
          cardType: 'CONSUMER',
          cardScheme: 'VISA',
          cardFormat: 'PHYSICAL',
          userRef: '602c18ca-866f-49ff-827f-cab21c8a6b5f',
          cardHolder: {
            firstName: 'Alex',
            lastName: 'CH',
          },
        },
        {
          id: 'V120012EQG',
          maskedNo: '412345******1501',
          expiry: '2024-06',
          status: 2,
          currency: 'GBP',
          cardLimit: '100.00',
          cardMaxLimit: '1000000.00',
          cardSpend: '0.00',
          cardType: 'CONSUMER',
          cardScheme: 'VISA',
          cardFormat: 'VIRTUAL',
          userRef: 'A1200B5PJY',
          cardHolder: {
            firstName: 'Alex',
            lastName: 'CH',
          },
        },
      ],
      totalPages: 1,
      page: 1,
      totalsize: 2,
    };

    return of(v);
  }

  getTransactionDetail(
    transactionId: string,
    transactionType: TransactionType,
  ): Observable<TransactionDetailResponse> {
    return of({
      currency: 'GBP',
      amount: 150,
      id: 'P2100DYH8X',
      dateOfTransaction: '2023-11-30T07:00:38.038+0000',
      reference: 'P2100DYH8X',
      createdAt: '2024-01-11T16:04:15.317Z',
      sender: {
        accountNumber: '12345678',
        name: 'Anne Miller',
        sortCode: '000000',
        ledgerNumber: '44230004',
      },
      receiver: {
        accountNumber: '12345678',
        sortCode: '000000',
        name: 'Brandon Taylor',
        iban: null,
        ledgerNumber: null,
      },
      status: 'success',
      feeAmount: 12,
      feeCurrency: 'GBP',
      totalAmount: 13,
      paymentReference: 'test',
      type: 'debit',
    });
  }

  subscribeUser(): Observable<boolean> {
    return of(true);
  }

  transferMoneyInternational(payload: TransferMoneyRequest): Observable<TransferMoneyInternationalResponse> {
    return of({
      currency: 'GBP',
      amount: 1000,
      feeAmount: 5,
      totalAmount: 1005,
      receiverName: 'Brandon Taylor',
      receiverAccountNumber: '1357924580',
      senderName: 'Anne Miller',
      senderAccountNumber: '1234567890',
    });
  }

  pdfDownload(accountId: string, type: string, startDate: string, endDate: string): Observable<any> {
    return of();
  }

  public getTransferReasons(accountId: string, country: string): Observable<TransferReason[]> {
    return of([
      {
        id: '1',
        description: 'Bill Payment',
      },
      {
        id: '2',
        description: 'Gift',
      },
    ]);
  }

  public getBulkPaymentHistory(
    page: number,
    size: number,
    searchValue: string,
  ): Observable<BulkPaymentHistoryResponse> {
    return of({
      page: 1,
      limit: 10,
      result: [
        {
          InactiveBeneficiaryCheck: true,
          accountCurrency: 'USD',
          accountId: '9301a25d-4ca6-4a07-a6c8-8ce9e1082a1b',
          accountName: 'Donald Trump',
          created_at: '2024-05-06T12:29:59.067Z',
          executed_at: '2024-05-06T12:29:59.067Z',
          fileId: '5c12d8ff-1900-4218-8d0d-672fe10baa0a',
          fileName: 'bulk-payment-beneficiary-template (2).csv',
          id: 'dd339128-eb30-4ae3-ab84-9f9daa3a725b',
          status: 'pending',
          totalAmount: '0.00',
          totalDuplicatePayments: 0,
          totalErrors: 0,
          totalFailedPayments: 0,
          totalPayments: 0,
          totalSchedulePayments: 0,
          totalSuccessPayments: 0,
          totalUniqueBeneficiaries: 0,
          updated_at: '2024-05-06T12:29:59.067Z',
          userId: '2da3404f-a700-41ec-92cd-b204018e2f73',
        },
        {
          InactiveBeneficiaryCheck: true,
          accountCurrency: 'EUR',
          accountId: '50aa2cd0-75a4-4e92-9445-1a3657df6a8b',
          accountName: 'Donald Trump',
          created_at: '2024-05-06T12:43:57.105Z',
          executed_at: '2024-05-06T12:43:57.105Z',
          fileId: '53e77b40-3c43-49f0-b160-31335102190d',
          fileName: 'bulk-payment-beneficiary-template (2).csv',
          id: 'e4397958-8ed6-4195-bcba-92860edc085d',
          status: 'pending',
          totalAmount: '0.00',
          totalDuplicatePayments: 0,
          totalErrors: 0,
          totalFailedPayments: 0,
          totalPayments: 0,
          totalSchedulePayments: 0,
          totalSuccessPayments: 0,
          totalUniqueBeneficiaries: 0,
          updated_at: '2024-05-06T12:43:57.105Z',
          userId: '2da3404f-a700-41ec-92cd-b204018e2f73',
        },
      ],
      totalPages: 1,
      totalItems: 2,
    });
  }
  getBulkPaymentById(
    page: number,
    size: number,
    masterId: string,
    searchValue: string,
  ): Observable<PaginatedResponse<BulkPaymentResultList>> {
    return of({
      result: [
        {
          id: 'cdf9d714-3537-486a-b902-bd15c0094e7f',
          name: 'Romeo Combs',
          beneficiaryId: null,
          beneficiaryStatus: 'pending',
          beneficiaryExistStatus: 'pending',
          beneficiaryExistAndCopStatus: 'close_match',
          beneficiaryCoPStatus: 'pending',
          amount: '10.00',
          currency: 'GBP',
          scheduleType: 'instant',
          scheduleDate: '2024-05-07T09:19:35.573Z',
          transactionId: null,
          transactionReference: 'Test',
          transactionPaymentReason: 'Goods and Services',
          transactionStatus: 'pending',
          status: 'failed',
          created_at: '2024-05-07T09:19:35.580Z',
          executed_at: '2024-05-07T09:19:35.580Z',
          updated_at: '2024-05-07T09:19:35.644Z',
          beneficiaryBankAccounts: [
            {
              country: 'GB',
              currency: 'GBP',
              account_number: '12345678',
              sort_code: '123456',
              iban: null,
              bic: null,
              aba: null,
              ach: null,
              rtn_canada: null,
              clabe: null,
              bsb_code: null,
              ifsc: null,
              cnaps: null,
              bank_code: null,
              branch_code: null,
              created_at: '2024-05-07T09:19:35.590Z',
              updated_at: '2024-05-07T09:19:35.590Z',
              id: 'f415aece-472a-4da9-bbb6-a8e74a0ea356',
              address: {
                address_line_1: 'London Street 1',
                address_line_2: 'liverpool ',
                address_line_3: null,
                city: 'London',
                region: 'Liverpool',
                postal_code: 'NH2 NW1',
                country: 'GB',
                house_number: null,
                created_at: '2024-05-07T09:19:35.585Z',
                updated_at: '2024-05-07T09:19:35.585Z',
                id: 'd079aa7e-4b2f-40de-8d72-8d26f013078d',
              },
            },
          ],
          entityStyle: '',
          type: 'Personal',
        },
        {
          id: 'cdf9d714-3537-486a-b902-bd15c0094e70',
          name: 'John Doe',
          beneficiaryId: null,
          beneficiaryStatus: 'pending',
          beneficiaryExistStatus: 'new',
          beneficiaryExistAndCopStatus: 'not_matched',
          beneficiaryCoPStatus: 'pending',
          amount: '11.00',
          currency: 'GBP',
          scheduleType: 'instant',
          scheduleDate: '2024-04-07T09:19:35.573Z',
          transactionId: null,
          transactionReference: 'Test',
          transactionPaymentReason: 'Goods and Services',
          transactionStatus: 'pending',
          status: 'failed',
          created_at: '2024-05-07T09:19:35.580Z',
          executed_at: '2024-05-07T09:19:35.580Z',
          updated_at: '2024-05-07T09:19:35.644Z',
          beneficiaryBankAccounts: [
            {
              country: 'GB',
              currency: 'GBP',
              account_number: '12345670',
              sort_code: '123450',
              iban: null,
              bic: null,
              aba: null,
              ach: null,
              rtn_canada: null,
              clabe: null,
              bsb_code: null,
              ifsc: null,
              cnaps: null,
              bank_code: null,
              branch_code: null,
              created_at: '2024-05-07T09:19:35.590Z',
              updated_at: '2024-05-07T09:19:35.590Z',
              id: 'f415aece-472a-4da9-bbb6-a8e74a0ea350',
              address: {
                address_line_1: 'London Street 2',
                address_line_2: 'liverpool ',
                address_line_3: null,
                city: 'London',
                region: 'Liverpool',
                postal_code: 'NH2 NW1',
                country: 'GB',
                house_number: null,
                created_at: '2024-05-07T09:19:35.585Z',
                updated_at: '2024-05-07T09:19:35.585Z',
                id: 'd079aa7e-4b2f-40de-8d72-8d26f0130780',
              },
            },
          ],
          entityStyle: '',
          type: 'Personal',
        },
        {
          id: '2fbe29cb-2efb-41e6-a96f-7f9a38cc9909',
          name: 'Abbas Cline',
          beneficiaryId: null,
          beneficiaryStatus: 'pending',
          beneficiaryExistStatus: 'pending',
          beneficiaryExistAndCopStatus: 'close_match',
          beneficiaryCoPStatus: 'duplicate records',
          amount: '10.00',
          currency: 'GBP',
          scheduleType: 'instant',
          scheduleDate: '2024-05-07T09:19:35.573Z',
          transactionId: null,
          transactionReference: 'Test',
          transactionPaymentReason: 'Goods and Services',
          transactionStatus: 'pending',
          status: 'duplicate records',
          created_at: '2024-05-07T09:19:35.596Z',
          executed_at: '2024-05-07T09:19:35.596Z',
          updated_at: '2024-05-07T09:19:35.647Z',
          beneficiaryBankAccounts: [
            {
              country: 'GB',
              currency: 'GBP',
              account_number: '12345678',
              sort_code: '123456',
              iban: null,
              bic: null,
              aba: null,
              ach: null,
              rtn_canada: null,
              clabe: null,
              bsb_code: null,
              ifsc: null,
              cnaps: null,
              bank_code: null,
              branch_code: null,
              created_at: '2024-05-07T09:19:35.605Z',
              updated_at: '2024-05-07T09:19:35.605Z',
              id: '0408fcb5-1bbc-4a2a-a941-4aff49670128',
              address: {
                address_line_1: 'London Street 1',
                address_line_2: 'liverpool ',
                address_line_3: null,
                city: 'London',
                region: 'Liverpool',
                postal_code: 'NH2 NW1',
                country: 'GB',
                house_number: null,
                created_at: '2024-05-07T09:19:35.600Z',
                updated_at: '2024-05-07T09:19:35.600Z',
                id: '5e24fcbd-44ee-48f0-a6ea-dff4e681de40',
              },
            },
          ],
          entityStyle: '',
          type: 'Business',
        },
        {
          id: 'cdf9d714-3537-486a-b902-bd15c0094e7f',
          name: "Taylor O'Moore",
          beneficiaryId: null,
          beneficiaryStatus: 'pending',
          beneficiaryExistStatus: 'pending',
          beneficiaryExistAndCopStatus: 'N/a',
          beneficiaryCoPStatus: 'not_applicable',
          amount: '10.00',
          currency: 'GBP',
          scheduleType: 'instant',
          scheduleDate: '2024-05-07T09:19:35.573Z',
          transactionId: null,
          transactionReference: 'Test',
          transactionPaymentReason: 'Goods and Services',
          transactionStatus: 'pending',
          status: 'failed',
          created_at: '2024-05-07T09:19:35.580Z',
          executed_at: '2024-05-07T09:19:35.580Z',
          updated_at: '2024-05-07T09:19:35.644Z',
          beneficiaryBankAccounts: [
            {
              country: 'GB',
              currency: 'GBP',
              account_number: '12345678',
              sort_code: '123456',
              iban: null,
              bic: null,
              aba: null,
              ach: null,
              rtn_canada: null,
              clabe: null,
              bsb_code: null,
              ifsc: null,
              cnaps: null,
              bank_code: null,
              branch_code: null,
              created_at: '2024-05-07T09:19:35.590Z',
              updated_at: '2024-05-07T09:19:35.590Z',
              id: 'f415aece-472a-4da9-bbb6-a8e74a0ea356',
              address: {
                address_line_1: 'London Street 1',
                address_line_2: 'liverpool ',
                address_line_3: null,
                city: 'London',
                region: 'Liverpool',
                postal_code: 'NH2 NW1',
                country: 'GB',
                house_number: null,
                created_at: '2024-05-07T09:19:35.585Z',
                updated_at: '2024-05-07T09:19:35.585Z',
                id: 'd079aa7e-4b2f-40de-8d72-8d26f013078d',
              },
            },
          ],
          entityStyle: '',
          type: 'Personal',
        },
        {
          id: 'cdf9d714-3537-486a-b902-bd15c0094e7f',
          name: 'Malachy Wallace',
          beneficiaryId: null,
          beneficiaryStatus: 'pending',
          beneficiaryExistStatus: 'pending',
          beneficiaryExistAndCopStatus: 'close_match',
          beneficiaryCoPStatus: 'approved',
          amount: '10.00',
          currency: 'GBP',
          scheduleType: 'instant',
          scheduleDate: '2024-05-07T09:19:35.573Z',
          transactionId: '123456789',
          transactionReference: 'Test',
          transactionPaymentReason: 'Goods and Services',
          transactionStatus: 'pending',
          status: 'success',
          created_at: '2024-05-07T09:19:35.580Z',
          executed_at: '2024-05-07T09:19:35.580Z',
          updated_at: '2024-05-07T09:19:35.644Z',
          beneficiaryBankAccounts: [
            {
              country: 'GB',
              currency: 'GBP',
              account_number: '12345678',
              sort_code: '123456',
              iban: null,
              bic: null,
              aba: null,
              ach: null,
              rtn_canada: null,
              clabe: null,
              bsb_code: null,
              ifsc: null,
              cnaps: null,
              bank_code: null,
              branch_code: null,
              created_at: '2024-05-07T09:19:35.590Z',
              updated_at: '2024-05-07T09:19:35.590Z',
              id: 'f415aece-472a-4da9-bbb6-a8e74a0ea356',
              address: {
                address_line_1: 'London Street 1',
                address_line_2: 'liverpool ',
                address_line_3: null,
                city: 'London',
                region: 'Liverpool',
                postal_code: 'NH2 NW1',
                country: 'GB',
                house_number: null,
                created_at: '2024-05-07T09:19:35.585Z',
                updated_at: '2024-05-07T09:19:35.585Z',
                id: 'd079aa7e-4b2f-40de-8d72-8d26f013078d',
              },
            },
          ],
          entityStyle: '',
          type: 'Personal',
        },
      ],
      limit: 10,
      page: 1,
      totalPages: 1,
      totalItems: 2,
      meta: {
        bulkPaymentsSummary: {
          totalPayments: 2,
          totalDuplicatePayments: 1,
          totalFailedPayments: 0,
          totalSuccessPayments: 0,
          totalSchedulePayments: 0,
          totalUniqueBeneficiaries: 0,
          currency: 'USD',
          totalAmount: 20.0,
          accountName: 'Donald Trump',
          accountId: '98766533',
          totalErrors: 0,
          executedAt: '2024-05-06T12:43:57.105Z',
          totalPendingPayments: 0,
          totalPendingBeneficiary: 0,
          masterStatus: 'complete',
        },
      },
    });
  }

  public getTotalWealth(baseCurrency: string): Observable<GetTotalWealthResponse> {
    return of({
      totalWealth: '6546.00',
    });
  }

  public getPaymentRequest(
    page: number,
    size: number,
    searchValue?: string,
  ): Observable<PaginatedResponse<PaymentRequestListResponse>> {
    return of({
      result: [
        {
          id: '66af85cc-b174-4f18-85dd-a9d69917baa6',
          paymentType: 'bulk',
          orgId: 'b5558138-7f3a-4a29-864d-634987486a77',
          orgName: 'test org',
          amount: 4.0,
          currency: 'GBP',
          paymentReference: 'Payment Reference value',
          paymentReason: '',
          paymentReasonId: '',
          approver: {
            userId: '',
            name: '',
          },
          beneficiary: {
            id: '',
            name: 'Multiple',
          },
          account: {
            id: '41beb46f-5c0c-48b5-b5b5-c1170e8fc514',
            name: 'GBP test account',
            accountHolderName: 'Donald Trump',
          },
          approverStatus: PaymentApproverStatus.PENDING,
          approvedOrRejectedDate: '2024-10-22T05:15:41.725Z',
          createdDate: '2024-10-22T05:15:41.725Z',
          requester: {
            userId: '18726037-f264-42f5-929f-9912c4dcd6e6',
            name: 'Donald',
          },
          typeId: 'dd339128-eb30-4ae3-ab84-9f9daa3a725b',
        },
        {
          id: '2ba79806-1516-42a4-92ce-ba2be438a5ae',
          paymentType: 'bulk',
          orgId: 'b5558138-7f3a-4a29-864d-634987486a77',
          orgName: 'test org',
          amount: 5.0,
          currency: 'GBP',
          paymentReference: '',
          paymentReason: '',
          paymentReasonId: '',
          approver: {
            userId: '',
            name: '',
          },
          beneficiary: {
            id: '',
            name: 'Multiple',
          },
          account: {
            id: '41beb46f-5c0c-48b5-b5b5-c1170e8fc514',
            name: 'GBP test account',
            accountHolderName: 'Donald Trump',
          },
          approverStatus: PaymentApproverStatus.APPROVED,
          approvedOrRejectedDate: '2024-10-22T05:15:41.725Z',
          createdDate: '2024-10-22T05:09:31.259Z',
          requester: {
            userId: '18726037-f264-42f5-929f-9912c4dcd6e6',
            name: 'John Doe',
          },
          typeId: 'dd339128-eb30-4ae3-ab84-9f9daa3a725b',
        },
        {
          id: '66af85cc-b174-4f18-85dd-a9d69917baa6',
          paymentType: 'bulk',
          orgId: 'b5558138-7f3a-4a29-864d-634987486a77',
          orgName: 'test org',
          amount: 6.0,
          currency: 'GBP',
          paymentReference: 'Lorem ipsum',
          paymentReason: '',
          paymentReasonId: '',
          approver: {
            userId: '',
            name: '',
          },
          beneficiary: {
            id: '',
            name: 'Multiple',
          },
          account: {
            id: '41beb46f-5c0c-48b5-b5b5-c1170e8fc514',
            name: 'GBP test account',
            accountHolderName: 'Donald Trump',
          },
          approverStatus: PaymentApproverStatus.PENDING,
          approvedOrRejectedDate: '2024-10-22T05:15:41.725Z',
          createdDate: '2024-10-22T05:15:41.725Z',
          requester: {
            userId: '18726037-f264-42f5-929f-9912c4dcd6e6',
            name: 'Anne Mille',
          },
          typeId: 'dd339128-eb30-4ae3-ab84-9f9daa3a725b',
        },
        {
          id: '2ba79806-1516-42a4-92ce-ba2be438a5ae',
          paymentType: 'bulk',
          orgId: 'b5558138-7f3a-4a29-864d-634987486a77',
          orgName: 'test org',
          amount: 8.0,
          currency: 'GBP',
          paymentReference: 'Lorem ipsum dolorsi',
          paymentReason: '',
          paymentReasonId: '',
          approver: {
            userId: '',
            name: '',
          },
          beneficiary: {
            id: '',
            name: 'Multiple',
          },
          account: {
            id: '41beb46f-5c0c-48b5-b5b5-c1170e8fc514',
            name: 'GBP test account',
            accountHolderName: 'Donald Trump',
          },
          approverStatus: PaymentApproverStatus.APPROVED,
          approvedOrRejectedDate: '2024-10-22T05:15:41.725Z',
          createdDate: '2024-10-22T05:09:31.259Z',
          requester: {
            userId: '18726037-f264-42f5-929f-9912c4dcd6e6',
            name: 'Brandon Taylor',
          },
          typeId: 'dd339128-eb30-4ae3-ab84-9f9daa3a725b',
        },
      ],
      limit: 10,
      page: 1,
      totalPages: 1,
      totalItems: 5,
    });
  }

  public getPaymentSummaryCounters(queryParams: string[]): Observable<PaymentRequestSummaryCounts> {
    return of({
      accountsCounter: 12,
      lastDayTotalCounter: 230,
      pendingCounter: 28,
      lastDayApprovalCounter: 200,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getDepositAccountInfo(type: DepositDetailsType[]): Observable<TermDepositAccountDetailsType> {
    return of({
      depositAmount: 100,
      fee: 10,
      feeRate: 10,
      currency: 'EUR',
      totalUnit: 10,
      initialExchangeRate: 1,
      initialFundAmount: 120,
      pendingInitialFundAmount: 10,
      fundGrowthPercentage: 10,
      fundGrowthValue: 10,
      currentExchangeRate: 10,
      currentExchangeRateCreatedDate: '2024-05-07T09:19:35.573Z',
      lastInterestCredit: {
        date: '2024-05-07T09:19:35.573Z',
        amount: 10,
        currency: 'EUR',
      },
      totalBalance: {
        currency: 'EUR',
        amount: 110,
      },
      totalInterest: {
        currency: 'EUR',
        amount: 10,
        incrementPercentage: 1,
      },
      totalBalanceWithInterest: {
        currency: 'EUR',
        amount: 110,
      },
    });
  }

  public getCancellablePayments(
    pageNumber = 1,
    limit = 10,
  ): Observable<PaginatedResponse<CancellablePaymentResponse>> {
    return of({
      result: [
        {
          id: '9c49254b-038d-40bf-bb74-f59f3c2e72f9',
          data: {
            bid: '6f4e3806-6d79-48b0-8226-d8fa59c640f6',
            name: 'John Doe',
            amount: 100,
            bankId: 'bdd19f48-f85b-41eb-bbc1-1301db8fefff',
            userId: 'd6f8e562-e19f-45a7-8ea1-9bccc0981831',
            currency: 'GBP',
            sortCode: '112561',
            accountType: 'personal',
            accountNumber: '28556991',
            sourceAccountId: '4328f347-f7ed-4ea9-b16b-02eabef036b7',
            paymentReference: 'testing',
            transferReasonId: '5e0a7759-1c2f-4f96-ae31-29e824f47958',
            transferReasonIdText: 'Payment for goods and services',
            destinationIdentifier: {
              id: 'bdd19f48-f85b-41eb-bbc1-1301db8fefff',
              country: 'GB',
              currency: 'GBP',
              sortCode: '112561',
              accountNumber: '28556991',
            },
            paymentReasonCategory: '',
          },
        },
      ],
      page: pageNumber,
      limit: limit,
      totalItems: 1,
      totalPages: 1,
    } as unknown as PaginatedResponse<CancellablePaymentResponse>);
  }

  /**
   * The plain GET /beneficiaries listing — no view-type routing and no
   * recently-paid merge, unlike getBeneficiary. Rows come from the same seed so
   * the two listings agree.
   */
  listBeneficiaries(params: {
    page?: number;
    size?: number;
    name?: string;
    currency?: string;
    country?: string;
    accountNumber?: string;
  }): Observable<PaginatedResponse<GetBeneficiaryResponse>> {
    return this.getBeneficiary(params.page ?? 1, params.size ?? 20).pipe(
      map((response) => {
        const filtered = response.result.filter((beneficiary) => {
          if (params.name && !beneficiary.name?.toLowerCase().includes(params.name.toLowerCase())) {
            return false;
          }
          if (params.currency && beneficiary.destinationIdentifier?.currency !== params.currency) {
            return false;
          }
          if (params.country && beneficiary.destinationIdentifier?.country !== params.country) {
            return false;
          }
          if (
            params.accountNumber &&
            beneficiary.destinationIdentifier?.accountNumber !== params.accountNumber
          ) {
            return false;
          }
          return true;
        });
        return { ...response, result: filtered, totalItems: filtered.length };
      }),
    );
  }

  /**
   * POST /payments. The same body answers an executed transfer, a future-dated
   * job and one inside a cancellation window, so a scheduled request echoes
   * back `scheduledAt` the way the wire does.
   */
  transferMoney(payload: TransferMoneyRequest): Observable<TransferMoneyResponse> {
    const response: TransferMoneyResponse = {
      id: 'c1d0d2a4-8f10-4a1e-9c1b-7b4f6f5b6a21',
      amount: +payload.amount,
      currency: 'GBP',
      feeAmount: 0,
      totalAmount: +payload.amount,
      receiverName: 'Brandon Taylor',
      receiverAccountNumber: '1357924580',
      senderName: 'John Doe',
      senderAccountNumber: '23456811',
      paymentReference: payload.paymentReference,
      transactionNumber: '20260114-PMT4KQ',
    };
    if (payload.schedulePaymentDate) {
      response.scheduledAt = payload.schedulePaymentDate;
    }
    return of(response);
  }

  /**
   * POST /beneficiaries/check-payee. The mock always reports a close match, and
   * dispatches the same COP state the ngxs mock data provider seeds so the
   * no-match review screens have something to render.
   */
  checkPayee(checkPayeeRequest: Payee, beneficiaryFlowParam?: string): Observable<CheckPayeeResponse> {
    this.store?.dispatch(new SetCopResponseStateName('Suggested Test User'));
    this.store?.dispatch(new SetCopResponseStateMatchStatusCode(CheckPayeeMatchStatus.CLOSE_MATCH));
    return of({
      name: 'Suggested Test User',
      matchStatus: true,
      matchStatusCode: CheckPayeeMatchStatus.CLOSE_MATCH,
      verificationToken: 'e3a4a1a2-5f6b-4c1d-9d2e-7a8b9c0d1e2f',
    } as CheckPayeeResponse);
  }

  // POST /beneficiaries answers the created beneficiary; only `id` is read.
  addBeneficiary(
    addBeneficiaryRequest: AddBeneficiaryRequest,
    accessToken?: string,
  ): Observable<GetBeneficiaryResponse> {
    return of({
      id: '3f2a7c88-1b45-4a2f-9f0e-5d6c7b8a9e01',
      name: addBeneficiaryRequest.name,
      accountType: addBeneficiaryRequest.accountType ?? ACCOUNTTYPE.PERSONAL,
      createdDate: '2026-01-14T10:15:00.000Z',
      destinationIdentifier: addBeneficiaryRequest.destinationIdentifier,
      address: addBeneficiaryRequest.address,
    } as GetBeneficiaryResponse);
  }

  // DELETE /payments/payment-reversal/:id answers no meaningful body.
  public removeScheduledPayment(txnId: string): Observable<any> {
    return of({ id: txnId, status: 'cancelled' });
  }

  // GET /payments/request/:id — keyed off the getPaymentRequest listing above
  // so the detail and the row agree on amount, currency and account.
  getPaymentRequestDetailsById(requestId: string): Observable<PaymentRequestSummary> {
    return of({
      id: requestId,
      amount: '4.00',
      currency: 'GBP',
      paymentReference: 'Payment Reference value',
      paymentReason: 'Bill Payment',
      requester: {
        userId: '18726037-f264-42f5-929f-9912c4dcd6e6',
        name: 'Donald',
        accountId: '41beb46f-5c0c-48b5-b5b5-c1170e8fc514',
        accountName: 'GBP test account',
        destinationIdentifier: {
          accountNumber: '23456811',
          'sort-code': '352434',
        },
      },
      approver: {
        userId: 'd6f8e562-e19f-45a7-8ea1-9bccc0981831',
        name: 'John Doe',
      },
      beneficiary: {
        name: 'Brandon Taylor',
        destinationIdentifier: {
          accountNumber: '1357924580',
          'sort-code': '223344',
        },
      },
      approverStatus: PaymentApproverStatus.PENDING,
      approvedOrRejectedDate: '2024-10-22T05:15:41.725Z',
      status: 'pending',
      createdDate: '2024-10-22T05:15:41.725Z',
      recordId: '66af85cc-b174-4f18-85dd-a9d69917baa6',
    });
  }

  // POST /payments/request answers only the created request's id.
  makePaymentRequest(payload: PaymentRequestorRequest): Observable<PaymentRequestorResponse> {
    return of({ id: '7b1c4e39-2d58-4f6a-8c31-0a9e2f4b6d17' });
  }

  // GET /beneficiaries/payment-schemes — the rails this mock's GB/GBP seed data
  // supports, plus the international ones for non-GBP beneficiaries.
  public getPaymentSchemes(
    countryCode?: string,
    currencyCode?: string,
  ): Observable<{ label: string; value: PaymentRailOptions }[]> {
    return of([
      { label: 'Faster Payments', value: 'faster-payments' as PaymentRailOptions },
      { label: 'CHAPS', value: 'chaps' as PaymentRailOptions },
      { label: 'SEPA', value: 'sepa' as PaymentRailOptions },
      { label: 'SWIFT', value: 'swift' as PaymentRailOptions },
    ]);
  }

  // POST /bulk-payments/:masterId/transfer-payments/:id/retry.
  retryBulkPaymentTransfer(masterId: string, id: string): Observable<any> {
    return of({ id, masterId, status: 'pending' });
  }
}
